import Component from '@glimmer/component';
import pageTitle from 'ember-page-title/helpers/page-title';
import { concat, fn } from '@ember/helper';
import PageSetup from 'bcl/components/page-setup';
import displayDate from 'bcl/helpers/display-date';
import sort from 'bcl/helpers/sort';
import { on } from '@ember/modifier';
import { Input } from '@ember/component';
import Modal from 'bcl/components/modal';
import filterSort from 'bcl/helpers/filter-sort';
import WaitingSpinner from 'bcl/components/waiting-spinner';
import type OrderModel from 'bcl/models/order';
import { service } from '@ember/service';
import type RouterService from '@ember/routing/router-service';
import type SessionService from 'bcl/services/session';
import type { Store } from '@warp-drive/core';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import type OrderEntryModel from 'bcl/models/order-entry';
import type LocationModel from 'bcl/models/location';
import CompanyModel from 'bcl/models/company';
import type UserModel from 'bcl/models/user';

export interface OrdersAcceptSignature {
  // The arguments accepted by the component
  Args: {
    model: OrderModel;
  };
  // Any blocks yielded by the component
  Blocks: {
    default: [];
  };
  // The element to which `...attributes` is applied in the component template
  Element: null;
}

export default class OrdersAcceptComponent extends Component<OrdersAcceptSignature> {
  @service declare router: RouterService;
  @service declare session: SessionService;
  @service declare store: Store;

  @action
  cancel() {
    // reset all models
    this.args.model.orderEntries.forEach((item: OrderEntryModel) =>
      item.rollbackAttributes()
    );
    this.router.transitionTo('auth.orders');
  }
  @tracked showAcceptModal = false;
  @tracked locations: LocationModel[] = [];
  @tracked isAccepting = false;
  @action
  async acceptPrepare() {
    this.isAccepting = true;
    // open modal which location to insert into
    const onlyNumbers = this.args.model.orderEntries
      .map(
        (item: OrderEntryModel) =>
          !Number.isNaN(Number.parseInt(item.amount_delivered))
      )
      .every((item) => item);
    if (!onlyNumbers) {
      alert('Es sind nicht nur Zahlen in den angenommenen Mengen eingetragen.');
      this.isAccepting = false;
      return;
    }
    this.locations = this.store.peekAll('location');
    const company = this.session.company;
    if (company?.default_location?.id) {
      await this.accept(company.default_location.id);
    } else {
      this.showAcceptModal = true;
    }
  }
  @action
  closeModal() {
    this.showAcceptModal = false;
  }
  @action
  async accept(locationId: string) {
    const location = this.store.peekRecord<LocationModel>(
      'location',
      locationId
    );
    if (!location) {
      console.error('Orders:Accept');
      return;
    }
    await acceptOrder(this.args.model, location, this.session.user, this.store);
    this.closeModal();
    this.isAccepting = false;
    this.router.transitionTo('auth.orders');
  }
  <template>
    {{pageTitle (concat "Bestellung " @model.id " überprüfen")}}
    <PageSetup @title={{concat "Bestellung " @model.id " überprüfen"}}>
      <div class="block">

        geplantes Liefer-Datum:
        {{displayDate this.args.model.delivery_date}}
      </div>
      <div class="block">
        Lieferant:
        {{this.args.model.supplier.name}}
      </div>
      <div class="px-4 lg:px-8">
        <div class="mt-8 flow-root">
          <div class="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
            <div class="inline-block min-w-full py-2 align-middle px-4 lg:px-8">
              <table class="min-w-full divide-y divide-gray-300">
                <thead>
                  <tr>
                    <th
                      scope="col"
                      class="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-3"
                    >Name</th>
                    <th
                      scope="col"
                      class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                    >Gebinde</th>
                    <th
                      scope="col"
                      class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                    >bestellt</th>
                    <th
                      scope="col"
                      class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                    >geliefert</th>

                  </tr>
                </thead>
                <tbody class="bg-white">
                  {{#each
                    (sort @model.orderEntries "position")
                    as |orderEntry|
                  }}
                    <tr class="even:bg-gray-50">
                      <td
                        class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-3"
                      >{{orderEntry.supplier_item.item.name}}</td>
                      <td
                        class="whitespace-nowrap px-3 py-4 text-sm text-gray-500"
                      >{{orderEntry.supplier_item.item.gebinde}}</td>
                      <td
                        class="whitespace-nowrap px-3 py-4 text-sm text-gray-500"
                      >{{orderEntry.amount}}</td>
                      <td
                        class="whitespace-nowrap px-3 py-4 text-sm text-gray-500"
                      >

                        <label
                          for="{{concat 'geliefert' orderEntry.number}}"
                          class="block text-sm font-medium leading-6 text-gray-900 sm:pt-1.5 sm:text-right sr-only"
                        >Gelieferte Menge für {{orderEntry.name}}</label>

                        <Input
                          id="{{concat 'geliefert' orderEntry.number}}"
                          @type="text"
                          @value={{orderEntry.html_amount_delivered}}
                          class="inline ml-4 w-16 rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:max-w-xs sm:text-sm sm:leading-6"
                        />
                      </td>

                    </tr>
                  {{/each}}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
      <div class="mt-6 flex items-center justify-end gap-x-6">
        <div class="ml-4">
          <button
            {{on "click" this.cancel}}
            type="button"
            class="text-sm font-semibold leading-6 text-gray-900"
          >abbrechen</button>
          <button
            {{on "click" this.acceptPrepare}}
            type="button"
            class="ml-2 rounded-md
              {{if this.isAccepting 'bg-gray-600' 'bg-indigo-600'}}
              px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
          >annehmen
            {{#if this.isAccepting}}
              <WaitingSpinner class="inline ml-2" />
            {{/if}}
          </button>

        </div>
      </div>
    </PageSetup>
    {{#if this.showAcceptModal}}
      <Modal @close={{this.closeModal}}>
        <div class="block">
          <h2 class="text-xl font-bold">Bestellung annehmen</h2>
          <p>In welches Lager soll die Bestellung übernommen werden?</p>
          <div class="mt-4">

            <button
              type="button"
              {{on "click" this.closeModal}}
              class="inline-flex text-sm font-semibold text-gray-900 border-2 border-gray-500 rounded-md px-2 py-1.5"
            >abbrechen</button>
            {{#each (filterSort this.locations "active" "name") as |location|}}

              <button
                type="button"
                {{on "click" (fn this.accept location.id)}}
                class="inline-flex justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
              >{{location.name}}</button>
            {{/each}}
          </div>
        </div>
        <div class="mt-5 sm:mt-6">
        </div>
      </Modal>
    {{/if}}
  </template>
}
export async function acceptOrder(
  model: OrderModel,
  location: LocationModel,
  user: UserModel,
  store: Store
) {
  model.accepted_by = user;
  model.accepted_date = new Date();
  model.status = 'delivered';
  model.delivered_to = location;
  model.delivered_as_expected = model.orderEntries
    .map((item: OrderEntryModel) => item.amount === item.amount_delivered)
    .every((item) => item);

  const promises = model.orderEntries
    .filter((oe: OrderEntryModel) => oe.hasDirtyAttributes)
    .map((item: OrderEntryModel) => item.save());
  await Promise.all(promises);
  await model.save();
}
